import moment from 'moment'

import { useContext, useState } from 'react';

import { gql } from '@apollo/client';
 
import { MusicNote } from '@mui/icons-material';

import { useQuery } from '@apollo/client';

import { Breadcrumbs, Link as MaterialLink, Typography, Container, Paper, Grid, Switch, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';

import { NodeTable } from '@/components/NodeTable';

import i18n from '@/i18n';

import { Link } from '@/components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Statistics } from '@/components/Statistics';
import { DiscoveryModeContext } from '@/contexts/DiscoveryModeContext';
import { ErrorView } from '@/components/ErrorView';
import { VBox } from '@/components/VBox';
import { HBox } from '@/components/HBox';
import { Case, SwitchCase } from '@/components/Syntax';
import { VictoryPie } from 'victory';
import { LoadingPage } from '@/components/LoadingPage';

const TOP_LIST_QUERY = gql`
  query getDiscoveryModeTopList(
    $organizationIds: [ID!],
    $labelIds: [ID!],
    $artistIds: [ID!],
    $breakdown: String,
    $sortBy: String,
    $stage: String,
    $campaignId: ID!,
  ) {
    spotify {
      discoveryModeCampaign(campaignId: $campaignId) {
        rows(
          organizationIds: $organizationIds,
          labelIds: $labelIds,
          breakdown: $breakdown,
          artistIds: $artistIds,
          sortBy: $sortBy,
          stage: $stage
        ) {
          id
          name
          type
          artistName
          labelName
          promotedStreams
          promotedStreamsUplift
          platformStreams
          platformStreamsUplift
          spotifyId
          intentRate
          label {
            id
            slug
            name
            fugaId
          }
          organization {
            id
            slug
            name
          }
          artists {
            slug
            name
          }
          royalty
          amount
          price
          discount
          fee
          net
          organizationId
          labelId
          releaseId
          labelFugaId
        }
      }
    }  
  }
`

const TRENDS_QUERY = gql`
  query getDiscoveryModeTrends(
    $organizationIds: [ID],
    $labelIds: [ID]
  ) {
    spotify {
      discoveryMode {
        trends(
          organizationIds: $organizationIds,
          labelIds: $labelIds,
        ) {
          rows {
            campaignId
            date
            badStreams
            promotedStreams
          }
        }
      }
    }  
  }
`

export function DiscoveryModeTopListContainer({
  organizationIds,
  labelIds,
  artistIds,
  releaseIds,
  sortBy='-_intent_rate',
  breakdown='track'
}) {
  const [topListViewType, setTopListViewType] = useState('list')
  const [campaignId, setCampaignId] = useState('latest')

  const { data, loading, previousData } = useQuery(
    TOP_LIST_QUERY,
    {
      variables: {
        campaignId,
        organizationIds,
        artistIds,
        labelIds,
        releaseIds,
        breakdown,
        sortBy
      }
    }
  )

  const recordings = (data ?? previousData)?.spotify?.discoveryModeCampaign?.rows?.slice(0, 20)

  if (!recordings) {
    return (
      <ErrorView />
    )
  }

  return (
    <VBox>
      <HBox>
        <Typography sx={{ flex: 1 }} level="h1" component="h1">{i18n.t('top-spotify-discovery-mode-tracks')}</Typography>
        <Typography sx={{ flex: 1 }} level="h1" component="h1">{i18n.t('latest-campaign')}</Typography>
        <Switch
          checked={campaignId === 'all'}
          onClick={(event) => {
            setCampaignId(event.target.checked ? 'all' : 'latest')
          }}
        />
        <Typography sx={{ flex: 1 }} level="h1" component="h1">{i18n.t('all-time')}</Typography>
        
        <Switch
          checked={topListViewType === "pie"}
          onClick={() => {
            setTopListViewType(topListViewType === "list" ? 'pie' : "list")
          }}
        />
      </HBox>
      <SwitchCase value={topListViewType}>
        <Case match="list">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{i18n.t('name')}</TableCell>
                <TableCell>{i18n.t('artist')}</TableCell>
                <TableCell>{i18n.t('label')}</TableCell>
                <TableCell>{i18n.t('promoted-streams')}</TableCell>
                <TableCell>{i18n.t('organization')}</TableCell>
                <TableCell>{i18n.t('intent-rate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recordings?.slice(0, 20).map((recording, i) => (
                <TableRow key={recording.id}>
                  <TableCell sx={{ textAlign: 'right' }}>{i + 1}</TableCell>
                  <TableCell>{recording.name}</TableCell>
                  <TableCell>{recording.artistName}</TableCell>
                  <TableCell>{recording.labelName}</TableCell>
                  <TableCell>{recording.promotedStreams}</TableCell>
                  <TableCell>{recording.organization?.name}</TableCell>
                 <TableCell sx={{ textAlign: 'right' }}>{recording.intentRate?.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Case>
        <Case match="pie">
          {loading ? (
            <LoadingPage />
          ) : (recordings instanceof Array ? (
            <VictoryPie
              colorScale={'qualitative'}
              radius={100}
              style={{
                labels: {
                  fontSize: 5
                }
              }}
              data={
                recordings?.map(
                  r => ({
                    x: r.name,
                    y: parseInt(r.intentRate)
                  })
                )
              }
            />
          ) : <ErrorView />)}
        </Case>
      </SwitchCase>
    </VBox>
  )
}


export function DiscoveryModeOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    start,
    end,
    selectedOrganizations,
    selectedLabels,
    selectedArtists,
    selectedRecordings,
    breakdown,
    stage
  } = useContext(DiscoveryModeContext)

  return (
    <Container maxWidth="xl">
      <Breadcrumbs aria-label="breadcrumb">
        <Typography>{i18n.t('discovery-mode')}</Typography>
        <MaterialLink as={Link} to={{ pathname: '/dashboard/discovery-mode/campaigns', search: searchParams.toString() }}>{i18n.t('campaigns')}</MaterialLink>
      </Breadcrumbs>
      <Typography>{i18n.t('discovery-mode-stats-for-the-latest-x-months', { numberOfMonths: 6 })}</Typography>
      <Paper>
        <DiscoveryModeTrendsContainer
          start={start?.format('YYYY-MM-DD')}
          end={end?.format('YYYY-MM-DD')}
          organizations={selectedOrganizations}
          labels={selectedLabels}
          artists={selectedArtists}
        />
      </Paper>
      <br />
      <Paper> 
        <DiscoveryModeTopListContainer
          organizationIds={selectedOrganizations?.map(o => o.slug)}
          labelIds={selectedLabels?.map(l => l.slug)}
          artistIds={selectedArtists?.map(a => a.slug)}
        /> 
      </Paper>
    </Container>
  )
}

export function DiscoveryModeTrendsContainer({
  start,
  end,
  organizations,
  labels,
  artists,
}) {
  const navigate = useNavigate()
  const { data, loading, previousData } = useQuery(TRENDS_QUERY, {
    variables: {
      start: moment(start)?.format('YYYY-MM-DD'),
      end: moment(end)?.format('YYYY-MM-DD'),
      organizationIds: organizations?.map(o => o.slug),
      organizationId: organizations?.[0]?.slug,
      labelIds: labels?.map(l => l.slug),
      artistIds: artists?.map(a => a.slug)
    }
  })

  const trends = (data ?? previousData)?.spotify?.discoveryMode?.trends

  if (trends?.rows) {
    return (
      <Statistics
        style={{
          tickLabels: { 
            angle: -35, 
            fontWeight: 'bold',
            fontFamily: "DM Sans",
            textAnchor: 'end',
            fontSize: 8
          }
        }}
        legend={[
          { 
            name: i18n.t('promoted-streams'),
            symbol: {
              strokeWidth: 0,
              fill: '#0077ff'
            }
          },
          {
            name: i18n.t('non-compensating-streams'),
            symbol: {
              strokeWidth: 0,
              fill: '#ff8800'
            }
          }
        ]}
        series={[
          {
            name: i18n.t('complete-streams'),
            id: 'promotedStreams',
            yKey: 'promotedStreams',
            xKey: (row) => moment(row.date).format('MMMM YYYY'),
            style: {
              data: {
                strokeWidth: 0,
                fill: '#0077ff'
              }
            },
            onBarClick: (bar) => {
              navigate(`/dashboard/discovery-mode/campaign/${bar.campaignId}`)
            }
          },
          {
            name: i18n.t('non-compensating-streams'),
            id: 'badStreams',
            yKey: 'badStreams',
            xKey: (row) => moment(row.date).format('MMMM YYYY'),
            style: {
              data: {
                strokeWidth: 0,
                fill: '#ff8800'
              }
            },
            onBarClick: (bar) => {
              navigate(`/dashboard/discovery-mode/campaign/${bar.campaignId}`)
            }
          }
        ]}
        onRowClick={(row) => {
          const searchParams = new URLSearchParams()
          organizations?.map(organization => searchParams.append('organization', JSON.stringify(organization)))
          labels?.map(label => searchParams.append('label', JSON.stringify(label)))
          artists?.map(artist => searchParams.append('artist', JSON.stringify(artist)))
          console.log("searchParams", searchParams)
          navigate({ pathname: `/dashboard/discovery-mode/campaign/${row.campaignId}`, search: searchParams.toString()})
        }}
        yTickFormat={(value) => value?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        yKey={(row) => row.promotedStreams}
        xKey={(row) => moment(row.date).format('MMMM YYYY')}
        labelKey={(row) => {
          if (row._stack == 2) {
            return i18n.t('x-bad-streams', { date: row.date, badStreams: row.badStreams.toLocaleString() })
          } /*else if (row._stack == 2) {
            return i18n.t('total-streams', { date: row.date, streams: (row.badStreams + row.promotedStreams).toLocaleString() })
          }*/
          return i18n.t('x-promoted-streams', { date: row.date, streams: row.badStreams + row.promotedStreams, promotedStreams: row.promotedStreams.toLocaleString() })
        }}
        labels={trends?.rows.map(row => moment(row.date).format('MMMM YYYY  '))}
        data={{ 
          stats: trends?.rows
        }}
      />
    )
  }
}

export default DiscoveryModeOverviewPage;
